import React, {memo, useRef, useState} from "react";
import {Dashboard} from "api/model/dashboard/Dashboard";
import {Toast} from "primereact/toast";
import Editor from "@monaco-editor/react";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import * as monaco from "monaco-editor";
import {
    PageLayout,
    TimingOptions
} from "api/model/dashboard/DashboardReportOptions";
import {getObjAttribute, Leaves, setObjAttribute} from "utils/utils";
import { Checkbox } from "primereact/checkbox";


type PageLayoutKey = Leaves<PageLayout>
type DashboardKey = Leaves<Dashboard>
type TimingOptionsKey = Leaves<TimingOptions>

export const DashboardReportEditor = (props: { dashboard: Dashboard, onChange: (e: Dashboard) => void,  cancel: () => void}) => {
    const [def, setDef] = useState<string | undefined>(JSON.stringify(props.dashboard, null, 2));
    const [config, setConfig] = useState<Dashboard>(props.dashboard);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const toast = useRef<Toast>(null);

    const handleSave = () => {
        props.onChange(config);
    }

    const updateConfig = (dashboard: Dashboard) => {
        setConfig(dashboard);
        setDef(JSON.stringify(dashboard, null, 2));
    }

    const onEditorChange = (newValue: string | undefined, e: monaco.editor.IModelContentChangedEvent) => {
        setDef(newValue);
        try {
            const val = JSON.parse(newValue!!);
            setConfig(val);
            setErrorMessage(undefined);
        } catch (e: any) {
            setErrorMessage("Failed to parse JSON: " + e.message);
        }
    }

    const onChangePageLayout = (pageLayout: PageLayout) => {
        const cfgNew = {...config, reportOptions: {...config.reportOptions, pageLayout: pageLayout}}
        updateConfig(cfgNew);
    }

    const onChangeGeneral = (dashboard: Dashboard) => {
        const cfgNew = {...config, ...dashboard}
        updateConfig(cfgNew);
    }

    const onChangeTimingOptions = (timingOptions: TimingOptions) => {
        const cfgNew = {...config, reportOptions: {...config.reportOptions, timingOptions: timingOptions}}
        updateConfig(cfgNew);
    }

    return <div className="flex flex-column">
        <Toast ref={toast}/>

        <div className="flex-grow-1 justify-content-center" style={{height: "60vh", overflowY: "scroll"}}>
            <form style={{width: 600, margin: "auto"}}>
                <GeneralSubForm dashboard={config} onChange={onChangeGeneral}/>
                <TimingOptionsSubForm timingOptions={config?.reportOptions.timingOptions} onChange={onChangeTimingOptions}/>
                <PageLayoutSubForm pageLayout={config?.reportOptions.pageLayout!!} onChange={onChangePageLayout}/>
            </form>

            { errorMessage && <div className="p-error">{errorMessage}</div> }
            <div className="mb-2"/>
            <Editor
                height="500px"
                width="100%"
                defaultLanguage="json"
                value={def}
                onChange={onEditorChange}
                theme="vs-dark"
            />
        </div>


        <div className="flex-none flex justify-content-center gap-2 mt-2" style={{width: "100%"}}>
            <Button label="Save" className="p-button-success w-full" icon="pi pi-check" onClick={handleSave} disabled={(errorMessage != undefined)}/>
            <Button label="Cancel" className="p-button-danger w-full" icon="pi pi-times" onClick={props.cancel}/>
        </div>
    </div>
};


const GeneralSubForm = (props: {dashboard: Dashboard, onChange: (o: Dashboard) => void}) => {
    const onGeneralChange = (prop : DashboardKey, value: any) => {
        const newDashboard = {...props.dashboard}
        setObjAttribute(newDashboard, prop, value)
        props.onChange(newDashboard)
    }

    return <React.Fragment>
        <h5 className="mb-5">General</h5>
        <div className="grid">
            <span className="p-float-label col-12 md:col-5">
                <Input.Text id="input-dashboard-title" label="Title" property={"title"} sourceObj={props.dashboard} onChange={onGeneralChange}/>
            </span>

            <span className="p-float-label col-12 md:col-5">
                <Input.Text id="input-dashboard-icon-class" label="Icon class" property={"iconClass"} sourceObj={props.dashboard} onChange={onGeneralChange}/>
            </span>

            <span className="col-12 md:col-2">
                <Input.Boolean id="input-dashboard-company-shared" label="Company Shared" property={"companyShared"} sourceObj={props.dashboard} onChange={onGeneralChange}/>
            </span>
        </div>
    </React.Fragment>
}

const PageLayoutSubForm = (props: {pageLayout: PageLayout, onChange: (o: PageLayout) => void}) => {
    const onChangePageLayout = (prop : PageLayoutKey, value: any) => {
        const newPageLayout = {...props.pageLayout}
        setObjAttribute(newPageLayout, prop, value)
        props.onChange(newPageLayout)
    }

    return <React.Fragment>
        <h5 className="mb-5">Page Layout</h5>
        <div className="grid">
            <span className="p-float-label col-12 md:col-6">
                <Input.Text id="input-page-layout-size" label="Page format (size)" property={"format.pageSize"} sourceObj={props.pageLayout} onChange={onChangePageLayout}/>
            </span>

            <span className="p-float-label col-12 md:col-6">
                <Input.Text id="input-page-layout-orientation" label="Page orientation" property={"format.orientation"} sourceObj={props.pageLayout} onChange={onChangePageLayout}/>
            </span>
        </div>

        <h5 className="mb-5">Margin Boxes</h5>
        <div className="grid">
            <span className="p-float-label col-12 md:col-3">
                <Input.Text id="input-page-layout-margin-boxes-top" label="Top" property={"margins.top"} sourceObj={props.pageLayout} onChange={onChangePageLayout}/>
            </span>

            <span className="p-float-label col-12 md:col-3">
                <Input.Text id="input-page-layout-margin-boxes-bottom" label="Bottom" property={"margins.bottom"} sourceObj={props.pageLayout} onChange={onChangePageLayout}/>
            </span>

            <span className="p-float-label col-12 md:col-3">
                <Input.Text id="input-page-layout-margin-boxes-left" label="Left" property={"margins.left"} sourceObj={props.pageLayout} onChange={onChangePageLayout}/>
            </span>

            <span className="p-float-label col-12 md:col-3">
                <Input.Text id="input-page-layout-margin-boxes-right" label="Right" property={"margins.right"} sourceObj={props.pageLayout} onChange={onChangePageLayout}/>
            </span>
        </div>
    </React.Fragment>
}

const TimingOptionsSubForm = (props: {timingOptions: TimingOptions, onChange: (o: TimingOptions) => void}) => {
    const onChangeTimingOptions = (prop : TimingOptionsKey, value: any) => {
        const newTimingOptions = {...props.timingOptions}
        setObjAttribute(newTimingOptions, prop, value)
        props.onChange(newTimingOptions)
    }

    return <React.Fragment>
        <h5 className="mb-5">Timing Options</h5>
        <div className="grid">
            <span className="p-float-label col-12 md:col-12 mb-4">
                <Input.Text id="input-timing-options-gen-cron" label="Generation CRON (Quartz)" property={"generationCron"} sourceObj={props.timingOptions} onChange={onChangeTimingOptions}/>
            </span>

            <span className="p-float-label col-12 md:col-4 mb-4">
                <Input.Text id="input-timing-options-truncate-to" label="Reference time truncate (align) unit" property={"referenceTimeTruncateTo"} sourceObj={props.timingOptions} onChange={onChangeTimingOptions}/>
            </span>

            <span className="p-float-label col-12 md:col-4 mb-4">
                <Input.Number id="input-timing-options-offset" label="Reference time offset" property={"referenceTimeOffset"} sourceObj={props.timingOptions} onChange={onChangeTimingOptions}/>
            </span>

            <span className="p-float-label col-12 md:col-4 mb-4">
                <Input.Text id="input-timing-options-offset-unit" label="Reference time offset unit" property={"referenceTimeOffsetUnit"} sourceObj={props.timingOptions} onChange={onChangeTimingOptions}/>
            </span>

            <span className="p-float-label col-12 md:col-12">
                <Input.Text id="input-timing-options-datetime-format" label="Date time display format" property={"reportTimeFormat"} sourceObj={props.timingOptions} onChange={onChangeTimingOptions}/>
            </span>
        </div>
    </React.Fragment>
}


namespace Input {
    interface BaseProps<KT> {
        id: string
        label: string
        property: KT,
        sourceObj: any | undefined
        onChange: (prop: KT, value: any) => void
    }

    interface TextProps<KT> extends BaseProps<KT> {}
    interface BooleanProps<KT> extends BaseProps<KT> {}
    interface NumberProps<KT> extends BaseProps<KT> {}

    export function Text<KT>(props: TextProps<KT>) {
        const value = getObjAttribute(props.sourceObj, props.property, "")
        return<React.Fragment>
            <InputText id={props.id}
                       type="text"
                       className="p-inputtext-sm w-full"
                       placeholder={props.label}
                       value={value}
                       onChange={e => props.onChange(props.property, e.target.value)}
            />
            <label htmlFor={props.id}>{props.label}</label>
        </React.Fragment>
    }

    export function Boolean<KT>(props: BooleanProps<KT>) {
        const value = getObjAttribute(props.sourceObj, props.property, false)
        return <div className="flex align-items-center">
            <Checkbox id={props.id}
                         className=""
                         checked={value}
                         onChange={e => props.onChange(props.property, e.checked)}
            />
            <label className="ml-2" htmlFor={props.id}>{props.label}</label>
        </div>
    }

    export function Number<KT>(props: NumberProps<KT>) {
        const [valid, setValid] = useState(true)
        const value = getObjAttribute(props.sourceObj, props.property, "")

        const onChange = (e: any) => {
            if(e.target.value === "") {
                setValid(true)
                props.onChange(props.property, undefined)
                return
            }

            const newValue = parseInt(e.target.value)
            if (isNaN(newValue)) {
                setValid(false)
            } else {
                setValid(true)
                props.onChange(props.property, newValue)
            }
        }

        return<React.Fragment>
            <InputText id={props.id}
                       type="number"
                       className={"p-inputtext-sm w-full" + (valid ? "" : " p-invalid")}
                       placeholder={props.label}
                       value={value}
                       onChange={onChange}
             />
            <label htmlFor={props.id}>{props.label}</label>
        </React.Fragment>
    }

}
